import { request } from '@/common';
import globalStore from '@/store/global';
import userStore from '@/store/user';
import { storage } from '@q/solib-utils';
import MarketingStore from '@/store/marketing';
import { makeAutoObservable, runInAction } from 'mobx';
import { NavigateFunction } from 'react-router-dom';

export interface NoticeItem {
  nid: number;
  /**
  1 - 视频点赞消息
  2 - 用户关注消息
  3 - 视频评论消息
   */
  type: number;
  is_read: boolean;
  payload: string;
  is_delete: boolean;
  /** unix 毫秒时间戳 */
  create_time: number;
  data: {
    /** 点赞用户的QID */
    "qid": string;
    /** 信息流作品 ID  */
    "data_id": number;
    /** 视频 MID */
    "biz_id": string;
    /** 点赞资源的类型 */
    "biz_type": string;
    /** 点赞标题，一般为用户昵称 */
    "title": string;
    /** 消息内容，一般为配置内容 */
    "content": string;
    /** 视频是否删除 */
    "is_delete": string;
    /** 点赞头像，一般为用户头像；https://ns.chat.360.cn/zhaomi-so/1a153a7a7ae97a7e784086a91bba3e0e.png */
    "avatar": string;
    /** 视频封面；https://ns.chat.360.cn/zhaomi-so/2dacdd4e3f09d830b4d2898a529f32e4.jpg */
    "cover": string;
    /** 0-未关注、1-已关注、2-互相关注、3-对方是你的粉丝 */
    "follow_status": 0 | 1 | 2 | 3;
    /** 信息流的 url */
    "url": string;
    /** 评论消息id，中台侧评论 id */
    "cmt_id": string;
    /** 消息内容，一般为文本消息 */
    "message": string;
    /** 回复的评论 id，如果为一级评论则为空 */
    "pid": string;
    /** 信息流作品 ID */
    "work_id": number;
    /** 信息流作品标题 */
    "work_title": string;
    /** 附加消息，为 json 串，存储图片和视频作品信息 */
    "ext": string;
  }
}

const WORK_FROMHOME_KEY = 'work-from-home';

class Store {
  constructor() {
    makeAutoObservable(this);
    this.bindHistoryListener()
  }

  previousUrl = '';
  noticeList: NoticeItem[] = [];
  npt: string;
  hasUnread = false;
  fromHome: 'video' | 'image' = storage.getItem(WORK_FROMHOME_KEY, 'session') as 'video' || 'video';
  /** 会话ID，如果需要当前会话级别消重，可使用此参数，生成规则：会话期间唯一 */
  hsid = `${Date.now()}`;
  lastLocation = {
    video: '/square/video',
    image: '/square/image',
  };
  taskUpdate = false;
  noWatermarkUsable = ''; // 无水印下载

  setTaskUpdate(state: boolean) {
    this.taskUpdate = state;
  }

  getNoWatermarkUsable(type: 'video' | 'image') {
    if (this.noWatermarkUsable) return;
    if (type === 'image') {
      request
      .get('/img/vip/config?appsource=so&device_platform=Web')
      .then(res => {
        this.noWatermarkUsable = res?.data?.map?.no_watermark_usable;
      });
    } else {
      request.get('/video/model/cfg', {
        params: {
          appsource: 'so',
          device_platform: 'Web'
        }
      }).then(res => {
        this.noWatermarkUsable = res?.data?.map?.no_watermark_usable;
      });
    }
  }

  bindHistoryListener = () => {
    window.addEventListener("popstate", (e) => {
      console.error(e)
      runInAction(() => {
        this.lastLocation.video = null
      })
    });
  }

  getNoticeList(next = false) {
    // http://yapi.mobile.qihoo.net/project/117/interface/api/15598
    return request.get<{
      npt: string;
      list: NoticeItem[];
    }>('/notice/list?appsource=so&device_platform=Web', {
      params: {
        npt: next ? this.npt : undefined,
      },
    }).then((res) => {
      this.npt = res.data.npt;

      const { list } = res.data;
      list.forEach((item) => {
        try {
          item.data = JSON.parse(item.payload);
        } catch { }
      });
      if (next) {
        this.noticeList.push(...list);
      } else {
        this.noticeList = list;
      }

      if (this.noticeList.some((item) => !item.is_read && !item.is_delete)) {
        this.clearUnread();
      }
    });
  }

  clearUnread() {
    if (this.hasUnread) {
      // http://yapi.mobile.qihoo.net/project/117/interface/api/15597
      request.get('/notice/clean_unread?appsource=so&device_platform=Web', {
        params: {
          type: 'all',
          nid: this.noticeList.flatMap((item) => {
            if (item.is_read || item.is_delete) return [];
            return [item.nid];
          }).join(','),
        },
      });
    }
  }

  setLocation(url: string, isReplace = false) {
    const currentPath = window.location.pathname + window.location.search; // 获取当前地址栏路径

    this.previousUrl = currentPath; // 记录“当前页面”为“即将成为前一个页面”

    if (url.includes('video')) {
      this.setFromHome('video');
    } else if (url.includes('image')) {
      this.setFromHome('image');
    }
    this.lastLocation[this.fromHome] = url;
    // 营销云跳转
    if (MarketingStore.isMarketingCloud) {
      const newUrl = new URL(url, window.location.href);
      // 营销云来源参数补充
      if (!newUrl.searchParams.has(MarketingStore.PAGE_SOURCE)) {
        newUrl.searchParams.set(MarketingStore.PAGE_SOURCE, MarketingStore.SOURCE_VAL);
      }
      const path = newUrl.pathname + newUrl.search;
      (globalStore.navigate as NavigateFunction)(path, {
        replace: isReplace,
      });
      return;
    }
    (globalStore.navigate as NavigateFunction)(url, {
      replace: isReplace,
    });
    userStore.doWhenLogin();
  }

  toHome() {
    this.setLocation(`/square/${this.fromHome}`);
  }

  setFromHome(home: 'video' | 'image') {
    runInAction(() => {
      this.fromHome = home;
    });
    storage.setItem(WORK_FROMHOME_KEY, home, 'session');
  }
}

export default new Store();
